<template>
  <div class="side-panel" @dragover.prevent="dropOn" @drop.prevent="dropIn">
    <div class="side-panel-header">
      <h6 class="title">AI Docent<i class="icon icon--beta"></i></h6>
      <p class="description" v-html="$t(`mine.sideTab.${selectedTab}.description`)"></p>
      <div class="execute-btn" :class="{ ready: isReady }" @click="executeAction">
        <i :class="['icon', `icon--${selectedTab}-btn`]"></i>
        <p class="label">{{ $t(`mine.sideTab.${selectedTab}.run`) }}</p>
        <i v-if="isReady" class="icon icon--arrow-blue"></i>
      </div>
    </div>
    <div class="side-panel-body">
      <div class="select-meta">
        <div class="report-count">
          <p>
            {{ $t(`mine.sideTab.${selectedTab}.countLabel`) }}
            <span class="report-count-current">{{ selectedReport.length }}</span>
          </p>
          <button class="reset-btn" @click="resetSelected">
            <i class="icon icon--reset"></i>
          </button>
        </div>
        <p class="token-count" v-if="selectedTab === 'gpt'"><span class="token-count-current">{{ accumulatedTokens | thousand }}</span> / {{ maxToken| thousand }} {{ $t('mine.sideTab.tokens') }}</p>
      </div>
      <div class="dropdown-area">
        <div class="report-list" :class="{'enough': selectedReport.length >= 10}" v-if="selectedReport.length > 0">
          <template v-for="(report, i) in slicedReports">
            <div class="report"
                 :class="[report.type]"
                 :style="[{marginTop: i < slicedReports.length - 1 ? '-148px' : '0px',width: `${118 - (slicedReports.length - i - 1) * 4}px`}]"
                 :key="report.id">
              <template v-if="i === slicedReports.length - 1">
                <div class="report-title" v-html="selectedReportTitle(report)"></div>
                <div class="report-date">{{ setRegDate(report.regDate) }}</div>
              </template>
            </div>
          </template>
        </div>
        <div v-else class="dropdown-placeholder">
          <div class="drop-desc" v-html="$t('mine.sideTab.gpt.dropPlaceHolder')"></div>
          <div class="drop-icons">
            <i class="icon icon--report-doc"></i>
            <i class="icon icon--drop-in size-16"></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import AppDialog from '@/components/ui/AppDialog.vue'
import { create } from 'vue-modal-dialogs'
import { format, parse } from 'date-fns'
import store from '@/store'
import { mapMutations, mapState } from 'vuex'

export default {
  name: 'MinePanel',
  data () {
    return {
      maxToken: 180000,
      selectedTab: 'gpt'
    }
  },
  computed: {
    ...mapState([
      'selectedReport',
      'draggingItem',
      'isChatOpened'
    ]),
    slicedReports () {
      return this.selectedReport.slice(-10)
    },
    accumulatedTokens () {
      return this.selectedReport.reduce(
        (acc, report) => acc + (typeof report.tokens === 'number' ? report.tokens : 0),
        0
      )
    },
    isReady () {
      return !this.isChatOpened && this.selectedReport.length > 0
    }
  },
  methods: {
    ...mapMutations([
      'setIsChatOpened'
    ]),
    dropIn (e) {
      const report = JSON.parse(e.dataTransfer.getData('report'))

      if (this.accumulatedTokens + report.tokens > this.maxToken) {
        const tokenLimitDialog = create(AppDialog)
        const optionContent = {
          style: 'margin: -16px 0 42px 0;',
          text: `<a href="${this.$t('dialog.report.tokenLimit.linkUrl')}" target="_blank"
                style="display: flex; justify-content: center; align-items: center; gap: 4px; color: #0074FF; text-decoration: underline;">
                <i class="icon icon--ba-link blue" style="margin-bottom: 0;"></i>
                <span>${this.$t('dialog.report.tokenLimit.linkText')}</span>
                </a>`
        }
        tokenLimitDialog({
          dialogType: `report.tokenLimit`,
          optionContent
        })
        return
      }
      store.commit('setSelectedReport', report)
      this.dragOut()
    },
    dropOn () {

    },
    dragOut () {
      store.commit('clearDraggingItem')
      const ghostContainer = document.getElementById('mine-ghost')
      ghostContainer.classList.remove('visible')
    },
    executeAction () {
      this.setIsChatOpened(true)
    },
    resetSelected () {
      store.commit('clearSelectedReport')
    },
    selectedReportTitle (report) {
      if (report.reportType === 'weekly') {
        return this.weeklyDate(report)
      } else if (report.promptType === 'mashup') {
        return this.$t('mine.sideTab.mashupCardTitle')
      } else {
        return report.title
      }
    },
    setRegDate (utcDate) {
      const date = parse(utcDate, this.timezone)
      return format(date, 'YYYY-MM-DD HH:mm:ss')
    }
  }
}
</script>

<style scoped lang="scss">
$reportColor: #2683CB;
$reportType: (
  'journey': #8F74FA,
  'target': #8F74FA,
  'ranking': #6289EB,
  'trend': #00A5D9,
  'mashup': #E074E2
);
.side-panel {
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-radius: 16px;
  color: #fff;
  line-height: 1;
  padding: 24px;
  &-header {
    display: flex;
    flex-direction: column;
    gap: 16px;
    .title {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #FFF;
      font-size: 20px;
      font-weight: 700;
      padding: 0 5px;
    }
    .description {
      padding: 0 5px;
      font-weight: 500;
      font-size: 13px;
      line-height: 120%;
      color: #CCC;
    }
    .execute-btn {
      position: relative;
      display: flex;
      align-items: center;
      gap: 8px;
      pointer-events: none;
      border-radius: 8px;
      border: 2px solid #BBB;
      background: #1C1C1C;
      box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.06), 0px 1px 2px 0px rgba(0, 0, 0, 0.15);
      padding: 14px;
      opacity: 0.3;
      .icon {
        filter: brightness(0) invert(1);
      }
      .label {
        min-width: 101px;
        color: #FFF;
        font-size: 14px;
        font-weight: 600;
      }
      &.ready {
        @keyframes borderColorPulse {
          0% {
            border-color: #BBB;
          }
          50% {
            border-color: #0085FF;
          }
          100% {
            border-color: #BBB;
          }
        }
        @keyframes iconOpacityPulse {
          0% {
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            opacity: 0;
          }
        }
        cursor: pointer;
        pointer-events: all;
        opacity: 1;
        animation: borderColorPulse 1600ms ease-out 600ms infinite;

        .icon {
          filter: none;
        }
        .label {
          color: #fff;
        }
        .icon--arrow-blue {
          position: absolute;
          top: -4px;
          right: -36px;
          transform: translate(0, -100%);
          opacity: 0;
          animation: iconOpacityPulse 1600ms ease-out 600ms infinite;
        }

        &:hover {
          background-color: #222;
        }
      }
    }
  }
  &-body {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 15px;
    .select-meta {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 0 5px;
      .report-count {
        display: flex;
        align-items: center;
        justify-content: space-between;
        &-current {
          color: #FF2D92;
        }
        .reset-btn {
          display: flex;
        }
      }
      .token-count {
        color: #888;
        font-size: 12px;
        font-weight: 500;
        &-current {
          color: #ccc;
        }
      }
    }
    .dropdown-area {
      position: relative;
      width: 100%;
      min-height: 184px;
      padding: 10px;
      border-radius: 8px;
      border: 2px dashed #FFF;
      background-color: #666;
      box-shadow: 0px 0px 0px 5px #555 inset, 0px 8px 12px -2px rgba(0, 0, 0, 0.24);
      box-sizing: border-box;
      &.full {
        border-color: transparent;
        background-color: transparent;
        box-shadow: none;
      }
      .report-list {
        display: flex;
        flex-direction: column-reverse;
        align-items: center;
        width: 100%;
        height: 100%;
        &.enough {
          .report {
            &:nth-child(1) {
              background-color: #979797;
              opacity: 0.1;
            }
            &:nth-child(2) {
              background-color: #979797;
              opacity: 0.3;
            }
            &:nth-child(3) {
              background-color: #979797;
              opacity: 0.5;
            }
          }
        }
        .report {
          position: relative;
          width: 100%;
          height: 140px;
          border-radius: 8px;
          padding: 12px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          background-color: $reportColor;
          box-shadow: 0px 8px 12px -2px rgba(0, 0, 0, 0.24);

          @each $report, $value in $reportType {
            &.#{$report} {
              background-color: $value;
            }
          }

          &-title {
            display: -webkit-box;
            color: #FFF;
            text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.20);
            font-size: 12px;
            font-weight: 600;
            line-height: 1.5;
            letter-spacing: -0.5px;
            overflow: hidden;
            text-overflow: ellipsis;
            word-break: break-all;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 5;
          }
          &-date {
            color: #FFF;
            font-size: 12px;
            font-weight: 500;
            line-height: 1.2;
            letter-spacing: -0.6px;
            text-align: center;
            opacity: 0.72;
          }
        }
      }

      .dropdown-placeholder {
        min-height: 164px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 13px;
        width: 100%;
        height: 100%;
        .drop-desc {
          color: #FFF;
          text-align: center;
          font-size: 12px;
          font-weight: 500;
        }
        .drop-icons {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 2px;
        }
      }
      &.disabled::after {
        opacity: 0.3;
      }
    }
  }
  &-footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
    margin-top: 10px;
    font-size: 13px;
    text-align: center;
    .mashup-warning {
      line-height: 1.2;
      color: #FFE32D;
    }
    .mashup-guide {
      display: flex;
      align-items: center;
      gap: 4px;
      color: #D7D7D7;
    }
  }
}
</style>
